import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000ComboBoxBase from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2104RoleActionHandler } from "./U2104RoleActionHandlers";
import type { U2104LogicalUnit, U2104LogicalUnitAction } from "../../../../U2104Service";
import {
  u2104FlattenLogicalUnitCategoryGroups,
  u2104GroupLogicalUnitsByCategory,
} from "../../../../tools/u2104GroupLogicalUnitsByCategory";

interface U2104RoleActionDlgProps {
  u2104RoleActionHandler: U2104RoleActionHandler;
  t: TFunction;
  NS: string;
}

type U2104LogicalUnitComboItem = U2104LogicalUnit & { displayLabel: string };

function U2104RoleActionEditDlg({ u2104RoleActionHandler, t, NS }: U2104RoleActionDlgProps) {
  const { formStateHandlers } = u2104RoleActionHandler;
  const { i18n } = useTranslation();

  const filteredLogicalUnitActions = useMemo(() => {
    const filtered = u2104RoleActionHandler.logicalUnits.filter(
      (lu) =>
        !u2104RoleActionHandler.gridLogicalUnitCodes.includes(lu.logicalUnitCode) ||
        (formStateHandlers.getData() && lu.logicalUnitCode === formStateHandlers.getData().logicalUnitCode),
    );
    const groups = u2104GroupLogicalUnitsByCategory(filtered, {
      noCategoryLabel: t("lblNoLogicalUnitCategory", { ns: NS }),
      locale: i18n.language,
    });
    return u2104FlattenLogicalUnitCategoryGroups(groups).map(
      (lu): U2104LogicalUnitComboItem => {
        const groupLabel =
          groups.find((g) => g.items.some((i) => i.id === lu.id))?.label ?? t("lblNoLogicalUnitCategory", { ns: NS });
        return {
          ...lu,
          displayLabel: `${groupLabel} — ${lu.logicalUnitDescription}`,
        };
      },
    );
  }, [u2104RoleActionHandler.logicalUnits, u2104RoleActionHandler.gridLogicalUnitCodes, formStateHandlers, t, NS, i18n.language]);

  const areAllActionsChecked = useMemo(
    () =>
      u2104RoleActionHandler.logicalUnitActions.length > 0 &&
      u2104RoleActionHandler.logicalUnitActions.every((action) => action.idChecked),
    [u2104RoleActionHandler.logicalUnitActions],
  );

  return (
    <U2000SaveCancelDialog
      width="ax-w-2xl"
      height="h-[600px]"
      isOpen={u2104RoleActionHandler.isDialogOpen}
      onClose={u2104RoleActionHandler.onDlgClose}
      title={t("role.lblLogicalUnitAction", { ns: NS })}
      isValid={formStateHandlers.isValid()}
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBoxBase
          data={filteredLogicalUnitActions}
          keyField="id"
          descField="displayLabel"
          onChangeObject={u2104RoleActionHandler.onLogicalUnitChangeObject as (field: U2104LogicalUnitComboItem | null) => void}
          value={u2104RoleActionHandler.formStateHandlers.getData() as U2104LogicalUnitComboItem}
          disabled={u2104RoleActionHandler.formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
        />

        {u2104RoleActionHandler.logicalUnitActions && u2104RoleActionHandler.logicalUnitActions.length > 0 && (
          <div className="mb-2 flex items-center gap-x-2 border-b pb-2">
            <div
              role="checkbox"
              aria-checked={areAllActionsChecked}
              tabIndex={0}
              className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${areAllActionsChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
              onClick={() => u2104RoleActionHandler.onCheckAllLogicalUnitActions()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  u2104RoleActionHandler.onCheckAllLogicalUnitActions();
                }
              }}
            >
              {areAllActionsChecked && (
                <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <p className="inline-block font-semibold text-gray-800">{t("role.lblSelectAll", { ns: NS })}</p>
          </div>
        )}

        {u2104RoleActionHandler.logicalUnitActions &&
          u2104RoleActionHandler.logicalUnitActions.map((action: U2104LogicalUnitAction) => (
            <div key={action.id} className="mb-1 flex items-center gap-x-2">
              <div
                role="checkbox"
                aria-checked={action.idChecked}
                tabIndex={0}
                className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${action.idChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
                onClick={() => u2104RoleActionHandler.onCheckLogicalUnitAction(action)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    u2104RoleActionHandler.onCheckLogicalUnitAction(action);
                  }
                }}
              >
                {action.idChecked && (
                  <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <p className="inline-block text-gray-800">
                {action.logicalUnitActionCode} - {action.logicalUnitActionDescription}
              </p>
            </div>
          ))}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U2104RoleActionEditDlg;
